import { canViewModule } from '@/auth/modulePermission'
import type { DealDossierTab } from '@/store/dealDossierStore'

export const DOSSIER_NAV: { id: DealDossierTab; label: string; hint: string; countKey?: 'documents' | 'journal' | 'audit' }[] = [
  { id: 'overview', label: 'Overview', hint: 'Pipeline, KPIs, property, capital, deal identity and the request' },
  { id: 'property', label: 'Property', hint: 'Collateral, rent roll, comparables, property fields' },
  { id: 'economics', label: 'Economics', hint: 'Economics, structure and pricing, equity and returns' },
  { id: 'underwriting', label: 'Underwriting', hint: 'Models, field map, review, screening' },
  { id: 'capital_stack', label: 'Capital Stack', hint: 'Requested, recommended, approved layers' },
  { id: 'parties', label: 'Parties', hint: 'Borrower, sponsor, broker, deal team, party fields' },
  { id: 'documents', label: 'Documents', hint: 'Deal file uploads and the intake package', countKey: 'documents' },
  { id: 'journal', label: 'Journal', hint: 'Notes, calls, meetings', countKey: 'journal' },
  { id: 'audit', label: 'Audit', hint: 'Event trail, field lineage and notes', countKey: 'audit' },
  { id: 'field_map', label: 'Field Map', hint: 'What this deal holds, how it got there, what is missing and who has to move' },
]

/** The dossier a `/pipeline?deal=<id>[&tab=<tab>]` link opens: the named tab,
 *  else the Overview. Dashboard rows link with no tab; the host used to open
 *  nothing for them, leaving the Inspector on another deal. */
export function dossierLinkTarget(search: string): { loanId: string; tab: DealDossierTab } | null {
  const params = new URLSearchParams(search)
  const loanId = params.get('deal')
  if (!loanId) return null
  const named = DOSSIER_NAV.find(item => item.id === params.get('tab'))
  return { loanId, tab: named ? named.id : 'overview' }
}

/** A tab whose content belongs to a platform module, shown only to a role
 *  that may view that module. The Underwriting tab is Step-2 content
 *  (screening verdict, model panels); the Step-1 analyst has no Underwriting
 *  module (rbac.py ANALYST_STEP1_MODULES), so it is not theirs to see (C24). */
const TAB_MODULE: Partial<Record<DealDossierTab, string>> = {
  underwriting: 'underwriting',
}

type ModuleHolder = Parameters<typeof canViewModule>[0]

function tabAllowed(tab: DealDossierTab, user: ModuleHolder): boolean {
  const moduleId = TAB_MODULE[tab]
  return moduleId ? canViewModule(user, moduleId) : true
}

/** The dossier's tabs this account may see — the same module rule the
 *  sidebar uses (Layout.tsx canView). */
export function dossierNavFor(user: ModuleHolder): typeof DOSSIER_NAV {
  return DOSSIER_NAV.filter((item) => tabAllowed(item.id, user))
}

/** The tab to show: the requested one, or the Overview when the requested tab
 *  is out of this account's scope (a link, a saved tab, an in-page jump). */
export function tabInScope(tab: DealDossierTab, user: ModuleHolder): DealDossierTab {
  return tabAllowed(tab, user) ? tab : 'overview'
}
